import {
    ComposedChart, Area, XAxis, YAxis, Tooltip,
    ReferenceLine, ResponsiveContainer, CartesianGrid
} from "recharts";
import { useTheme, readCssVar } from "./ThemeContext";

// Literal hex (an alpha suffix is appended below); in dark mode each color is
// swapped for its theme token's resolved value.
const TYPE_META = [
    { key: "novelty", label: "Novelty", color: "#2a7a5e", token: "--accent-text" },
    { key: "method", label: "Methodology", color: "#5b8fa8", token: "--series-blue" },
    { key: "impact", label: "Impact", color: "#a86b3c", token: "--bronze" },
    { key: "overall", label: "Overall", color: "#1e3330", token: "--brand-text" },
];

// Bin midpoints for the 10 histogram bins: "0-9","10-19",...,"80-89","90-100"
const BIN_MIDS = [4.5, 14.5, 24.5, 34.5, 44.5, 54.5, 64.5, 74.5, 84.5, 95];

function normalPdf(x, mean, std) {
    if (!std || std <= 0) return 0;
    const z = (x - mean) / std;
    return Math.exp(-0.5 * z * z) / (std * Math.sqrt(2 * Math.PI));
}

// Build chart rows aligned to the histogram bins. Each row carries the bar count
// plus a "curve" value: the normal PDF sampled at the bin midpoint, scaled so the
// curve's peak matches the tallest histogram bar (keeps both visible together).
function buildChartData(dist) {
    const histogram = dist.histogram ?? [];
    const mean = dist.mean ?? 0;
    const std = dist.std ?? 0;
    const maxCount = histogram.reduce((m, h) => Math.max(m, h.count ?? 0), 0);
    const peakPdf = normalPdf(mean, mean, std); // PDF maximum (at the mean)
    const scale = peakPdf > 0 && maxCount > 0 ? maxCount / peakPdf : 0;
    return histogram.map((h, i) => ({
        bucket: h.bucket,
        count: h.count ?? 0,
        curve: scale > 0 ? normalPdf(BIN_MIDS[i], mean, std) * scale : 0,
    }));
}

// Map a 0..100 score to its bin midpoint so the ReferenceLine lands on the
// category axis (the x-axis uses bin labels, not raw scores).
function scoreToBucketLabel(score) {
    const idx = Math.min(9, Math.floor(score / 10));
    const histLabels = ["0-9", "10-19", "20-29", "30-39", "40-49", "50-59", "60-69", "70-79", "80-89", "90-100"];
    return histLabels[idx];
}

function percentileBadge(pct) {
    if (pct == null) return null;
    const top = Math.max(0, 100 - pct);
    // "Top X%" reads better for high performers; otherwise show the percentile.
    if (pct >= 50) return `Top ${top}%`;
    const suffix = pct % 10 === 1 && pct !== 11 ? "st"
        : pct % 10 === 2 && pct !== 12 ? "nd"
        : pct % 10 === 3 && pct !== 13 ? "rd" : "th";
    return `${pct}${suffix} percentile`;
}

function MiniDistribution({ meta, dist, score, percentile, scoreLabel }) {
    const chartData = buildChartData(dist);
    const badge = percentileBadge(percentile);
    return (
        <div className="submissions-card" style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--ink-900)" }}>{meta.label}</p>
                {badge && (
                    <span style={{
                        fontSize: 11, fontWeight: 600, color: meta.color,
                        background: `${meta.color}1a`, padding: "2px 8px", borderRadius: 6
                    }}>{badge}</span>
                )}
            </div>
            <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginBottom: 8 }}>
                {scoreLabel}: <strong style={{ color: meta.color }}>{score}</strong>
                {dist.mean != null && <> · Avg {dist.mean}</>}
            </p>
            <ResponsiveContainer width="100%" height={160}>
                <ComposedChart data={chartData} margin={{ top: 22, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 9, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                    <Tooltip
                        contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                        labelFormatter={(label) => `Score ${label}`}
                        formatter={(value) => [`~${Math.round(value)}`, "Papers"]}
                    />
                    <Area type="monotone" dataKey="curve" stroke={meta.color} strokeWidth={2}
                        fill={meta.color} fillOpacity={0.08} dot={false} name="Distribution" legendType="none" />
                    <ReferenceLine
                        x={scoreToBucketLabel(score)}
                        stroke={meta.color}
                        strokeWidth={2}
                        strokeDasharray="4 3"
                        label={{ value: "You", position: "top", fontSize: 10, fill: meta.color, fontWeight: 600 }}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

export default function ScoreDistribution({
    data,
    title = "How your paper compares",
    subtitle,
    scoreLabel = "Your score",
}) {
    const { theme } = useTheme();
    if (!data) return null;

    const typeMeta = theme === "dark"
        ? TYPE_META.map(m => ({ ...m, color: readCssVar(m.token) || m.color }))
        : TYPE_META;

    const { scores, percentiles, population, distribution } = data;
    const hasData = distribution && (population ?? 0) > 0
        && TYPE_META.some(m => distribution[m.key]?.histogram?.length);

    const derivedSubtitle = hasData
        ? `Where your scores fall across ${population} scored ${population === 1 ? "paper" : "papers"} on the platform`
        : "Score comparison";

    return (
        <div className="submissions-card" style={{ padding: "20px" }}>
            <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--ink-900)" }}>{title}</p>
            <p style={{ margin: 0, fontSize: 12, color: "var(--ink-300)", marginTop: 2 }}>
                {subtitle ?? derivedSubtitle}
            </p>

            {!hasData ? (
                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: "24px 12px" }}>
                    Not enough scored papers yet to compare your manuscript against the platform.
                </p>
            ) : (
                <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
                    {typeMeta.map(meta => {
                        const dist = distribution[meta.key];
                        if (!dist || !(dist.histogram?.length)) return null;
                        return (
                            <MiniDistribution
                                key={meta.key}
                                meta={meta}
                                dist={dist}
                                score={scores?.[meta.key] ?? 0}
                                percentile={percentiles?.[meta.key]}
                                scoreLabel={scoreLabel}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}
